import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../App';
import { useCountdown } from '../hooks/useCountdown';
import Icon from '../components/Icon';
import './Hub.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function fmt_date(d) {
  if (!d) return '';
  return new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

function EtoilesNote({ note = 5 }) {
  const n = Math.min(5, Math.max(0, note));
  return (
    <div className="temoignage-etoiles">
      {Array.from({ length: n }).map((_, i) => (
        <Icon key={i} name="etoile" size={14} weight="fill" color="currentColor" />
      ))}
      {Array.from({ length: 5 - n }).map((_, i) => (
        <Icon key={'v' + i} name="etoile" size={14} weight="regular" color="currentColor" />
      ))}
    </div>
  );
}

function EventLiveCard({ ev }) {
  return (
    <div className="event-live-card">
      <div className="event-live-badge">
        <span className="live-dot" />
        Précommandes ouvertes
      </div>
      <h2 className="event-live-nom">{ev.nom}</h2>
      <p className="event-live-date">
        {fmt_date(ev.date_event)}{ev.heure_debut ? ` · ${ev.heure_debut}` : ''}
        {ev.lieu ? ` · ${ev.lieu}` : ''}
        {ev.ville ? `, ${ev.ville}` : ''}
      </p>
      <div className="event-live-dispo">
        {ev.complet ? (
          <span className="badge-complet">Complet</span>
        ) : (
          <span className="badge-disponible">Places disponibles</span>
        )}
      </div>
      <a
        href={`/precommande/${ev.slug}`}
        className="notif-btn"
        style={{ display: 'inline-flex', textDecoration: 'none', alignSelf: 'flex-start' }}
      >
        <Icon name="panier" size={15} weight="bold" />
        Commander maintenant
      </a>
    </div>
  );
}

function Countdown({ targetDate, onExpire }) {
  const { jours, heures, minutes, secondes, fini } = useCountdown(targetDate);
  useEffect(() => {
    if (fini && onExpire) onExpire();
  }, [fini, onExpire]);
  if (fini) return null;
  return (
    <div className="countdown-bloc">
      <div className="countdown-label">Prochain événement dans</div>
      <div className="countdown-chiffres">
        <div className="countdown-unite">
          <span className="countdown-nombre">{String(jours).padStart(2, '0')}</span>
          <span className="countdown-nom">Jours</span>
        </div>
        <span className="countdown-sep">:</span>
        <div className="countdown-unite">
          <span className="countdown-nombre">{String(heures).padStart(2, '0')}</span>
          <span className="countdown-nom">Hrs</span>
        </div>
        <span className="countdown-sep">:</span>
        <div className="countdown-unite">
          <span className="countdown-nombre">{String(minutes).padStart(2, '0')}</span>
          <span className="countdown-nom">Min</span>
        </div>
        <span className="countdown-sep">:</span>
        <div className="countdown-unite">
          <span className="countdown-nombre">{String(secondes).padStart(2, '0')}</span>
          <span className="countdown-nom">Sec</span>
        </div>
      </div>
    </div>
  );
}

export default function Hub() {
  const navigate = useNavigate();
  const settings = useSettings();

  const [data, setData]             = useState(null);
  const [chargement, setChargement] = useState(true);

  const [email, setEmail]             = useState('');
  const [notifStatut, setNotifStatut] = useState(null);
  const [notifEnvoi, setNotifEnvoi]   = useState(false);

  const rechargerHub = React.useCallback(() => {
    fetch(`${API}/api/public/hub`)
      .then(r => r.json())
      .then(d => {
        if (d.events_live?.length === 1) {
          navigate(`/evenement/${d.events_live[0].slug}`);
          return;
        }
        setData(d);
        setChargement(false);
      })
      .catch(() => {
        setData({ events_live: [], events_a_venir: [], dates_annoncees: [], temoignages: [], has_live: false });
        setChargement(false);
      });
  }, [navigate]);

  useEffect(() => { rechargerHub(); }, [rechargerHub]);

  async function sInscrire(e) {
    e.preventDefault();
    if (!email.includes('@')) return;
    setNotifEnvoi(true);
    try {
      const res = await fetch(`${API}/api/public/liste-attente`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setNotifStatut('ok');
        setEmail('');
      } else {
        setNotifStatut('erreur');
      }
    } catch {
      setNotifStatut('erreur');
    } finally {
      setNotifEnvoi(false);
    }
  }

  /* ── Chargement ── */
  if (chargement) {
    return (
      <div className="hub-page">
        <div className="hub-hero">
          <div className="hub-hero-left">
            {settings.logo_url ? (
              <img
                src={settings.logo_url}
                alt={settings.nom_entreprise || 'Escamel'}
                className="hub-hero-logo"
                onError={e => { e.target.style.display = 'none'; }}
              />
            ) : (
              <span className="hub-hero-logo-text">ESCAMEL</span>
            )}
          </div>
        </div>
        <div className="hub-chargement">
          <div className="spinner-grand" />
        </div>
      </div>
    );
  }

  const prochainEvent  = data.events_a_venir?.[0];
  const dateCountdown  = prochainEvent?.date_event
    ? prochainEvent.date_event + 'T17:00:00'
    : null;

  const aEvenementsAvenir =
    (data.events_a_venir?.length > 0) || (data.dates_annoncees?.length > 0);

  return (
    <div className="hub-page">

      {/* ── HERO ASYMÉTRIQUE ── */}
      <div className="hub-hero">
        <div className="hub-hero-left">
          <div className="hub-hero-eyebrow">
            {settings.nom_entreprise || 'Escamel Artisans du Fumoir'}
          </div>

          {settings.logo_url ? (
            <img
              src={settings.logo_url}
              alt={settings.nom_entreprise || 'Escamel'}
              className="hub-hero-logo"
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : (
            <h1 className="hub-hero-titre">
              BBQ Artisan<br />du Fumoir
            </h1>
          )}

          {!settings.logo_url && (
            <p className="hub-hero-sous-titre">
              Viandes fumées au bois de cerisier, sans compromis sur la qualité.
            </p>
          )}
        </div>

        <div className="hub-hero-right">
          <div className="hub-hero-deco" />
        </div>
      </div>

      {/* ── MAIN ── */}
      <div className="hub-main">

        {/* CAS 2+ : plusieurs events live */}
        {data.events_live.length > 1 && (
          <section>
            <div className="hub-section-header">
              <span className="hub-section-label">Précommandes en cours</span>
            </div>
            <div className="hub-events-multi">
              {data.events_live.map(ev => (
                <EventLiveCard key={ev.id} ev={ev} />
              ))}
            </div>
          </section>
        )}

        {/* CAS 0 : aucun event live */}
        {data.events_live.length === 0 && (
          <section className="hub-revient-bientot">
            <div className="hub-revient-icone">
              <Icon name="fumage" size={40} weight="duotone" />
            </div>
            <h2 className="hub-section-titre">Revenez bientôt</h2>
            <p className="hub-section-desc">
              Notre prochain BBQ se prépare dans les coulisses.
            </p>
          </section>
        )}

        {/* Countdown */}
        {data.events_live.length === 0 && dateCountdown && (
          <Countdown targetDate={dateCountdown} onExpire={rechargerHub} />
        )}

        {/* Événements à venir + dates annoncées */}
        {aEvenementsAvenir && (
          <section className="dates-avenir">
            <div className="dates-titre">Événements à venir</div>

            {data.events_a_venir.map(ev => (
              <div className="date-item" key={ev.id}>
                <span className="date-icone">
                  <Icon name="date" size={18} />
                </span>
                <div>
                  <div className="date-principale">
                    {fmt_date(ev.date_event)}
                    {ev.lieu ? ` — ${ev.lieu}` : ''}
                  </div>
                  <div className="date-secondaire">
                    {ev.nom}
                    {ev.statut === 'publie' ? ' · Précommandes bientôt disponibles' : ''}
                  </div>
                </div>
                {ev.statut === 'publie' && (
                  <span className="date-badge-soon">Bientôt</span>
                )}
              </div>
            ))}

            {data.dates_annoncees.map((date, i) => (
              <div className="date-item" key={`annonce-${i}`}>
                <span className="date-icone">
                  <Icon name="date" size={18} />
                </span>
                <div>
                  <div className="date-principale">{date}</div>
                  <div className="date-secondaire">Date à confirmer</div>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Formulaire liste d'attente */}
        <section className="notif-section">
          <h3 className="notif-titre">Soyez notifié en premier</h3>
          <p className="notif-sous-titre">
            On vous écrit seulement quand les précommandes ouvrent.
          </p>
          {notifStatut === 'ok' ? (
            <div className="success" style={{ padding: '14px 0', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="succes" size={18} weight="fill" />
              Inscription confirmée. On vous avertit dès l'ouverture.
            </div>
          ) : (
            <form className="notif-form" onSubmit={sInscrire}>
              <input
                type="email"
                className="notif-input"
                placeholder="Votre courriel…"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="notif-btn" disabled={notifEnvoi}>
                {notifEnvoi ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span className="spinner" />
                    Envoi…
                  </span>
                ) : (
                  <>
                    <Icon name="envelope" size={15} weight="bold" />
                    M'avertir
                  </>
                )}
              </button>
            </form>
          )}
          {notifStatut === 'erreur' && (
            <div className="danger" style={{ marginTop: 10, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="erreur" size={15} weight="fill" />
              Une erreur s'est produite. Réessayez.
            </div>
          )}
          <p className="notif-privacy">
            <Icon name="verrou" size={12} />
            Aucun spam. Juste un courriel quand on ouvre.
          </p>
        </section>

        {/* Témoignages */}
        {data.temoignages.length > 0 && (
          <section className="temoignages-section">
            <div className="dates-titre">Ils en parlent</div>
            <div className="temoignage-grille">
              {data.temoignages.map(t => (
                <div className="temoignage-card" key={t.id}>
                  <EtoilesNote note={t.note || 5} />
                  <p className="temoignage-texte">&laquo;&nbsp;{t.texte}&nbsp;&raquo;</p>
                  <div className="temoignage-auteur">
                    {t.nom}{t.source ? ` · ${t.source}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* ── FOOTER ── */}
      <footer className="hub-footer">
        <div className="hub-footer-logo">ESCAMEL</div>
        <div className="hub-footer-sub">Artisans du Fumoir</div>
        <div className="hub-footer-links">
          <a href="mailto:commandes@escamel.ca" className="hub-footer-lien">
            <Icon name="email" size={14} />
            commandes@escamel.ca
          </a>
          <a href="https://escamel.ca" target="_blank" rel="noopener noreferrer" className="hub-footer-lien">
            <Icon name="lien" size={14} />
            escamel.ca
          </a>
          <a href="https://www.google.com/maps/search/?api=1&query=Stoneham-et-Tewkesbury+QC" target="_blank" rel="noopener noreferrer" className="hub-footer-lien">
            <Icon name="lieu" size={14} />
            Stoneham-et-Tewkesbury, QC
          </a>
        </div>
        <p className="hub-footer-corpo">
          Client corporatif ?{' '}
          <a href="/recherche-corpo">Retrouver votre soumission</a>
        </p>
      </footer>
    </div>
  );
}
